import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import { VideoLandingData } from "@app/routes/VideoLanding/utils";
import { fetchVideoLandingData } from "@app/routes/VideoLanding/VideoLandingPage.server";
import AdSettings from "@app/utils/AdSettings";
import { extractUrlRedirect } from "@app/utils/extractUrlRedirect";
import MetaData from "@components/Modules/MetaData";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { LoaderFunctionArgs, useLoaderData, useLocation } from "react-router";

import PubBaseLayout from "../BaseLayout";
import VideoLandingLayout from "../VideoLandingLayout/VideoLanding";

export const meta = () => {
  return MetaData({
    title: "Videos",
    type: "WebPage",
    path: "/video",
  });
};

export default function VideoLandingPage() {
  const data: TRouteWithoutRedirect<VideoLandingData, string> = useLoaderData();
  const location = useLocation();

  if (data.statusCode !== 200) {
    return <ErrorLayout response={data} />;
  }

  return (
    <PubBaseLayout>
      <AdSettings
        pageType="videos"
        sectionName="videos"
        adUnits="lb1,lb2,catfish,prestitial"
        pageTitle="Videos"
        currentLocation={location}
      />
      <VideoLandingLayout
        data={{
          name: data.payload.videoLandingTitle,
          subHeading: "",
          anchorListTitle: data.payload.anchorListTitle,
          anchorList: data.payload.anchorList,
          socialListTitle: data.payload.socialListTitle,
          socialList: data.payload.socialList,
        }}
      />
    </PubBaseLayout>
  );
}

export async function loader({
  request,
}: LoaderFunctionArgs): Promise<
  TRouteWithRedirect<VideoLandingData | GlobalBaseData, GlobalBaseData | string>
> {
  const data = await fetchVideoLandingData();

  if (data.type === ResponseType.CLIENT_ERROR && data.statusCode === 404) {
    const redirectResult = await extractUrlRedirect(request.url);

    if (redirectResult && redirectResult.target) {
      return redirectResult;
    }
  }

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}

/**
 * Client-only loader to prevent /storyRoute.data endpoint requests.
 *
 * React Router's default behavior on client-side navigation (via useNavigate):
 * - Attempts to fetch data from /storyRoute.data endpoint
 * - Results in 404 errors (endpoint doesn't exist)
 *
 * By exporting clientLoader:
 * - No requests are made to /storyRoute.data endpoint
 * - Data returned from /context.json
 * - Component handles all data fetching via useSWRInfinite
 */
export async function clientLoader({
  ...args
}: LoaderFunctionArgs & {
  serverLoader: <T = unknown>() => Promise<T>;
}) {
  const result = await fetchContext(args);

  if (
    !result ||
    typeof result !== "object" ||
    !("context" in result) ||
    !result.context
  ) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  return result.context;
}
